import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import PulseLoader from "react-spinners/PulseLoader";
import * as moment from "moment";
import DatePicker from "react-multi-date-picker";
import { Image } from "hesus-ui-kit";
import axios from "axios";
import AddEditCentreSummary from "../../../AddEditSummary/AddEditCentreSummary";
import PaginationData from "@shared/baseUI/PaginationData/PaginationData";
import {
  getDatePickerFormat,
  getDateFormat,
  getCalendarLocale,
} from "@utils/datePickerUtils";
import enableIcon from "@icons/ic-enable.svg";
import disableIcon from "@icons/ic-disable.svg";
import greySummaryIcon from "@icons/supplier-sumarry-grey.svg";
import blueSummaryIcon from "@icons/supplier-sumarry-blue.svg";
import darkBlueSummaryIcon from "@icons/supplier-sumarry-blue-dark.svg";
import darkBlueInvoicedIcon from "@icons/ic-invoiced.svg";
import "./CentreSummary.scss";
import {
  SummaryListContainer,
  FlexRow,
  SectionContainer,
  FilterFlexRow,
  FilterOuter,
  OuterWrapper,
  GridRow,
  SummaryServiceInfo,
  HeaderWrapperTable,
  HeaderGridRow,
  OrderServiceInfo,
  GridItem,
  GridSubItem,
  GridSubLabel,
} from "./CentreSummary.styled";
import {
  renderPriceWithCurrency,
  addSpace,
  getConvertedPrice,
} from "@utils/utils";

/**
 * Site - Summaries - Centres
 * Path - /site/view/{siteId}/summary
 * @see See [Site - Summaries - Centres Requirements](https://hesusstore.atlassian.net/wiki/spaces/HS/pages/3458007070/Site+-+Summaries+-+Centres)
 * @component
 */
const CentreSummary = (props) => {
  const { t, countryId, entityId } = props;

  const date = new Date();
  const y = date.getFullYear();
  const m = date.getMonth();
  const firstDay = new Date(y, m, 1);
  const lastDay = new Date(y, m + 1, 0);
  const [startDate, setStartDate] = useState(
    moment(new Date(firstDay)).format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(
    moment(new Date(lastDay)).format("YYYY-MM-DD")
  );

  const [summaryData, setSummaryData] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageStart, setPageStart] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [pageCount, setPageCount] = useState(0);
  const [invoiceStatus, setInvoiceStatus] = useState(0);
  const [isAddSummaryPopupVisible, setAddSummaryPopupVisible] = useState(false);
  const [summaryId, setSummaryId] = useState("");
  const [clientOrderItemId, setClientOrderItemId] = useState("");
  const [solutionId, setSolutionId] = useState("");
  const [editSummary, setEditSummary] = useState(false);
  const [isRefreshList, setIsRefreshList] = useState(false);
  const [centreName, setCentreName] = useState("");

  const getSummaryInfo = () => {
    setLoading(true);
    axios
      .get(
        `/centresSummaries?startDate=${moment(startDate).format(
          "YYYY-MM-DD"
        )}&endDate=${moment(endDate).format(
          "YYYY-MM-DD"
        )}&siteId=${entityId}&country=${countryId}&readyInvoicing=${invoiceStatus}&limit=${pageSize}&page=${currentPage}`
      )
      .then((response) => {
        setSummaryData(response.data.summaries);
        setPageCount(response.data.filteredRecords);
        setLoading(false);
      })
      .catch((error) => {
        setSummaryData([]);
        setPageCount(0);
        setLoading(false);
      });
  };

  useEffect(() => {
    getSummaryInfo();
  }, [startDate, endDate, invoiceStatus, currentPage, pageSize]);

  const onStartDateChange = (e) => {
    setStartDate(moment(e.toDate()).format("YYYY-MM-DD"));
  };

  const onEndDateChange = (e) => {
    setEndDate(moment(e.toDate()).format("YYYY-MM-DD"));
  };

  const changeInvoiceStatus = () => {
    setInvoiceStatus(!invoiceStatus);
  };

  const summaryAction = (isEdit, summaryId, itemId, companyId, centreName) => {
    setAddSummaryPopupVisible(true);
    setSummaryId(summaryId);
    setClientOrderItemId(itemId);
    setEditSummary(isEdit);
    setSolutionId(companyId);
    setCentreName(centreName);
  };

  const closeSummaryCreationPopup = () => {
    setAddSummaryPopupVisible(false);
    setSummaryId("");
    setClientOrderItemId("");
    setSolutionId("");
    setEditSummary(false);
  };

  const updateRefreshStatus = () => {
    setIsRefreshList(true);
  };

  useEffect(() => {
    if (isRefreshList) {
      getSummaryInfo();
      setIsRefreshList(false);
    }
  }, [isRefreshList]);

  return (
    <>
      <SummaryListContainer className="centre-summary-list">
        <OuterWrapper>
          <FlexRow>
            <SectionContainer>
              <div>
                <FilterFlexRow>
                  <FilterOuter>
                    <div className="centre-summary-list__date-container">
                      <label htmlFor="winning-date">
                        {t("SUMMARIES_FILTER_START_DATE")}
                      </label>

                      <DatePicker
                        inputClass="centre-summary-list__date-input"
                        containerClassName="centre-summary-list__datepicker-container"
                        calendarPosition="bottom-center"
                        onChange={onStartDateChange}
                        format={getDatePickerFormat()}
                        locale={getCalendarLocale()}
                        value={getDateFormat(startDate, "YYYY-MM-DD") || ""}
                        name="winning-date"
                      />
                    </div>

                    <div className="centre-summary-list__date-container">
                      <label htmlFor="winning-date">
                        {t("SUMMARIES_FILTER_END_DATE")}
                      </label>

                      <DatePicker
                        inputClass="centre-summary-list__date-input"
                        containerClassName="centre-summary-list__datepicker-container"
                        calendarPosition="bottom-center"
                        onChange={onEndDateChange}
                        format={getDatePickerFormat()}
                        locale={getCalendarLocale()}
                        value={getDateFormat(endDate, "YYYY-MM-DD") || ""}
                        name="winning-date"
                      />
                    </div>

                    <div className="centre-summary-list__status-wrapper">
                      <Image
                        src={invoiceStatus ? enableIcon : disableIcon}
                        alt={t("SUMMARIES_FILTER_READY_INVOICING")}
                        title={t("SUMMARIES_FILTER_READY_INVOICING")}
                        marginLeft="7px"
                        onClick={() => changeInvoiceStatus()}
                        className="centre-summary-list__image-icon"
                      />
                      <label className="label warehouses-list__active-label">
                        {t("SUMMARIES_FILTER_READY_INVOICING")}
                      </label>
                    </div>
                  </FilterOuter>
                </FilterFlexRow>
              </div>
              <OrderServiceInfo>
                <HeaderGridRow
                  column="10% 25% 25% 10% 20% 10%"
                  marginBottom="0px"
                  gridGap="0rem"
                  marginTop="0px"
                >
                  <GridItem
                    padding="8px"
                    textAlign="center"
                    fontWeight="700"
                    borderRight="1px solid #fff"
                  >
                    {t("SUMMARIES_LIST_DATE")}
                  </GridItem>
                  <GridItem
                    padding="8px"
                    textAlign="left"
                    fontWeight="700"
                    borderRight="1px solid #fff"
                  >
                    {t("SUMMARIES_LIST_SERVICE")}
                  </GridItem>
                  <GridItem
                    padding="8px"
                    textAlign="left"
                    fontWeight="700"
                    borderRight="1px solid #fff"
                  >
                    {t("SUMMARIES_SOLUTION")}
                  </GridItem>
                  <GridItem
                    padding="8px"
                    textAlign="center"
                    fontWeight="700"
                    borderRight="1px solid #fff"
                  >
                    {t("SUMMARIES_LIST_PLANNED")}
                  </GridItem>
                  <GridRow column="1fr" borderRight="1px solid #fff">
                    <GridItem
                      padding="8px"
                      textAlign="center"
                      fontWeight="700"
                      borderRight="1px solid #fff"
                    >
                      {t("SUMMARIES_LIST_REALISED")}
                    </GridItem>
                    <GridRow column="1fr 1fr">
                      <GridItem
                        padding="8px"
                        textAlign="center"
                        fontWeight="700"
                        borderRight="1px solid #fff"
                      >
                        {t("SUMMARIES_LIST_REALISED_LOADS")}
                      </GridItem>
                      <GridItem
                        padding="8px"
                        textAlign="center"
                        fontWeight="700"
                        borderRight="1px solid #fff"
                      >
                        {t("SUMMARIES_LIST_REALISED_VOLUMES")}
                      </GridItem>
                    </GridRow>
                  </GridRow>
                  <GridItem
                    padding="8px"
                    textAlign="center"
                    fontWeight="700"
                    borderRight="1px solid #fff"
                  >
                    &nbsp;
                  </GridItem>
                </HeaderGridRow>
                {summaryData?.length > 0 &&
                  summaryData?.map((summary, summaryIndex) => {
                    return (
                      <GridRow
                        column="10% 25% 25% 10% 10% 10% 10%"
                        key={`site-centre-summary-${summary.id}-${summaryIndex}`}
                      >
                        <GridSubItem
                          color="#0A4986"
                          padding="5px 5px 0px 10px"
                          fontWeight="400"
                          fontSize="16px"
                          alignContent="center"
                          textAlign="center"
                          borderRight="1px solid #767676"
                          borderLeft="1px solid #767676"
                          borderBottom="1px solid #767676"
                        >
                          {getDateFormat(summary.clientOrderDate, "YYYY-MM-DD")}
                        </GridSubItem>
                        <GridRow
                          column="70% 1fr"
                          borderRight="1px solid #767676"
                          borderBottom="1px solid #767676"
                        >
                          <GridRow>
                            <GridSubItem
                              color="#0A4986"
                              padding="5px 5px 0px 10px"
                              fontWeight="700"
                              fontSize="16px"
                              alignContent="left"
                            >
                              {summary?.service?.hesusService?.name}
                            </GridSubItem>
                            <GridSubItem
                              color="#474747"
                              padding="5px 5px 0px 10px"
                              fontWeight="400"
                              fontSize="14px"
                              alignContent="left"
                              fontStyle="italic"
                            >
                              {summary?.service?.product?.name}
                            </GridSubItem>
                          </GridRow>
                          <GridSubItem
                            color="#474747"
                            padding="5px 5px 0px 10px"
                            fontWeight="400"
                            fontSize="16px"
                            alignContent="center"
                          >
                            {renderPriceWithCurrency(
                              addSpace(
                                getConvertedPrice(
                                  summary?.service?.quoteServiceCurrency?.id,
                                  summary?.service?.sellingPrice,
                                  summary?.service?.exchangeRates
                                )
                              ),
                              t(summary?.service?.sellingPriceUnit),
                              summary?.service?.quoteCurrency?.symbol,
                              summary?.service?.quoteCurrency?.position
                            )}
                          </GridSubItem>
                        </GridRow>
                        <GridRow
                          column="70% 1fr"
                          borderRight="1px solid #767676"
                          borderBottom="1px solid #767676"
                        >
                          <GridRow>
                            <GridSubItem
                              color="#0A4986"
                              padding="5px 5px 0px 10px"
                              fontWeight="700"
                              fontSize="16px"
                              alignContent="left"
                            >
                              [{summary?.solution?.centre?.supplierName}]
                              {summary?.solution?.centre?.companyName}
                            </GridSubItem>
                            <GridSubItem
                              color="#474747"
                              padding="5px 5px 0px 10px"
                              fontWeight="400"
                              fontSize="14px"
                              alignContent="left"
                              fontStyle="italic"
                            >
                              {summary?.solution?.centreProduct?.product?.name}
                            </GridSubItem>
                          </GridRow>
                          <GridSubItem
                            color="#474747"
                            padding="5px 5px 0px 10px"
                            fontWeight="400"
                            fontSize="16px"
                            alignContent="center"
                          >
                            {/* {summary?.solution?.priceType === 2 &&
                              summary?.solution?.solutionTransportPrices?.[0]
                                ?.price} */}
                            {summary?.solution?.priceType === 2 &&
                              renderPriceWithCurrency(
                                addSpace(
                                  getConvertedPrice(
                                    summary?.service?.quoteServiceCurrency?.id,
                                    summary?.solution
                                      ?.solutionTransportPrices?.[0]?.price,
                                    summary?.service?.exchangeRates
                                  )
                                ),
                                t(
                                  summary?.solution
                                    ?.solutionTransportPrices?.[0]?.unit
                                ),
                                summary?.service?.quoteCurrency?.symbol,
                                summary?.service?.quoteCurrency?.position
                              )}
                            {summary?.solution?.priceType !== 2 &&
                              renderPriceWithCurrency(
                                addSpace(
                                  getConvertedPrice(
                                    summary?.service?.quoteServiceCurrency?.id,
                                    summary?.solution
                                      ?.solutionTreatmentPrices?.[0]?.price,
                                    summary?.service?.exchangeRates
                                  )
                                ),
                                t(
                                  summary?.solution
                                    ?.solutionTreatmentPrices?.[0]?.unit
                                ),
                                summary?.service?.quoteCurrency?.symbol,
                                summary?.service?.quoteCurrency?.position
                              )}
                          </GridSubItem>
                        </GridRow>
                        <GridSubItem
                          color="#0A4986"
                          padding="5px 5px 0px 10px"
                          fontWeight="400"
                          fontSize="16px"
                          alignContent="center"
                          textAlign="center"
                          borderRight="1px solid #767676"
                          borderBottom="1px solid #767676"
                        >
                          {summary?.totalPlannedQuantity +
                            " " +
                            t(summary?.totalPlannedUnit)}
                        </GridSubItem>
                        <GridSubItem
                          color="#0A4986"
                          padding="5px 5px 0px 10px"
                          fontWeight="400"
                          fontSize="16px"
                          alignContent="center"
                          textAlign="center"
                          borderRight="1px solid #767676"
                          borderBottom="1px solid #767676"
                        >
                          {(() => {
                            const hs = summary?.service?.hesusService;
                            const isTreatmentOnly =
                              !hs?.needCarrier &&
                              hs?.needCenter &&
                              !hs?.needCombination;
                            const hasBothServices =
                              (!hs?.needCarrier &&
                                hs?.needCenter &&
                                hs?.needCombination) ||
                              (hs?.needCarrier &&
                                !hs?.needCenter &&
                                hs?.needCombination) ||
                              (hs?.needCenter &&
                                hs?.needCarrier &&
                                !hs?.needCombination);

                            if (
                              isTreatmentOnly &&
                              summary?.realisedLoads?.quantity != null
                            ) {
                              return (
                                summary?.realisedLoads?.quantity +
                                " " +
                                t("LOAD")
                              );
                            }
                            if (
                              hasBothServices &&
                              summary?.haulageQuantity?.quantity != null &&
                              summary?.haulageQuantity?.unit
                            ) {
                              return (
                                summary?.haulageQuantity?.quantity +
                                " " +
                                t(summary?.haulageQuantity?.unit)
                              );
                            }
                            return "-";
                          })()}
                        </GridSubItem>
                        <GridSubItem
                          color="#0A4986"
                          padding="5px 5px 0px 10px"
                          fontWeight="400"
                          fontSize="16px"
                          alignContent="center"
                          textAlign="center"
                          borderRight="1px solid #767676"
                          borderBottom="1px solid #767676"
                        >
                          {/* {summary?.realisedVolume?.quantity} */}
                          {summary?.realisedVolume?.quantity !== "error" &&
                          summary?.realisedVolume?.quantity !== null
                            ? summary?.realisedVolume?.quantity +
                              " " +
                              t(summary?.realisedVolume?.unit)
                            : summary?.realisedVolume?.quantity === "error"
                            ? t("GENERAL_ERROR")
                            : "-"}
                        </GridSubItem>
                        <GridSubItem
                          color="#474747"
                          padding="5px 5px 0px 10px"
                          borderRight="1px solid #767676"
                          borderBottom="1px solid #767676"
                        >
                          {summary?.realisedLoads?.quantity == null &&
                            summary?.haulageQuantity?.quantity == null && (
                              <Image
                                src={greySummaryIcon}
                                alt={t("SUMMARIES_ACTION_TOOLTIP_NO_SUMMARY")}
                                title={t("SUMMARIES_ACTION_TOOLTIP_NO_SUMMARY")}
                                marginLeft="7px"
                                onClick={() =>
                                  summaryAction(
                                    false,
                                    "",
                                    summary?.clientOrderItemId,
                                    summary?.solution?.id,
                                    summary?.solution?.centre?.companyName
                                  )
                                }
                                className="centre-summary-list__image-icon"
                              />
                            )}
                          {(summary?.realisedLoads?.quantity != null ||
                            summary?.haulageQuantity?.quantity != null) &&
                            !summary.readyForInvoicing && (
                              <Image
                                src={blueSummaryIcon}
                                alt={t("SUMMARIES_ACTION_TOOLTIP_EDIT_SUMMARY")}
                                title={t(
                                  "SUMMARIES_ACTION_TOOLTIP_EDIT_SUMMARY"
                                )}
                                marginLeft="7px"
                                onClick={() =>
                                  summaryAction(
                                    true,
                                    summary?.summary?.id,
                                    summary?.clientOrderItemId,
                                    summary?.solution?.id,
                                    summary?.solution?.centre?.companyName
                                  )
                                }
                                className="centre-summary-list__image-icon"
                              />
                            )}
                          {(summary?.realisedLoads?.quantity != null ||
                            summary?.haulageQuantity?.quantity != null) &&
                            summary.readyForInvoicing &&
                            !summary.invoiced && (
                              <Image
                                src={darkBlueSummaryIcon}
                                alt={t("SUMMARIES_ACTION_TOOLTIP_EDIT_SUMMARY")}
                                title={t(
                                  "SUMMARIES_ACTION_TOOLTIP_EDIT_SUMMARY"
                                )}
                                marginLeft="7px"
                                onClick={() =>
                                  summaryAction(
                                    true,
                                    summary?.summary?.id,
                                    summary?.clientOrderItemId,
                                    summary?.solution?.id,
                                    summary?.solution?.centre?.companyName
                                  )
                                }
                                className="centre-summary-list__image-icon"
                              />
                            )}
                          {(summary?.realisedLoads?.quantity != null ||
                            summary?.haulageQuantity?.quantity != null) &&
                            summary.invoiced && (
                              <Image
                                src={darkBlueInvoicedIcon}
                                alt={t(
                                  "SUMMARIES_ACTION_TOOLTIP_EDIT_INVOICED"
                                )}
                                title={t(
                                  "SUMMARIES_ACTION_TOOLTIP_EDIT_INVOICED"
                                )}
                                marginLeft="7px"
                                onClick={() =>
                                  summaryAction(
                                    true,
                                    summary?.summary?.id,
                                    summary?.clientOrderItemId,
                                    summary?.solution?.id,
                                    summary?.solution?.centre?.companyName
                                  )
                                }
                                className="centre-summary-list__image-icon"
                              />
                            )}
                        </GridSubItem>
                      </GridRow>
                    );
                  })}
              </OrderServiceInfo>
              <SummaryServiceInfo>
                <GridRow marginBottom="0px" gap="0rem">
                  <HeaderWrapperTable>
                    {isLoading && (
                      <div className="suggestions-list-loading">
                        <PulseLoader color="#1bace4" size={15} />
                      </div>
                    )}
                    {!isLoading && summaryData?.length === 0 && (
                      <GridRow>
                        <GridSubLabel textAlign="center">
                          {t("NO_DATA")}
                        </GridSubLabel>
                      </GridRow>
                    )}
                  </HeaderWrapperTable>
                </GridRow>
              </SummaryServiceInfo>
            </SectionContainer>
          </FlexRow>

          {summaryData.length !== 0 && (
            <PaginationData
              pageCount={parseInt(pageCount, 10)}
              pageSize={pageSize}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              pageStart={pageStart}
              setPageStart={setPageStart}
              setPageSize={setPageSize}
            />
          )}
        </OuterWrapper>
        {isAddSummaryPopupVisible && (
          <AddEditCentreSummary
            id={summaryId}
            clientOrderItemId={clientOrderItemId}
            isEditMode={editSummary}
            siteId={entityId}
            onClose={closeSummaryCreationPopup}
            updateRefreshStatus={updateRefreshStatus}
            solutionId={solutionId}
            centreName={centreName}
          />
        )}
      </SummaryListContainer>
    </>
  );
};
CentreSummary.propTypes = {
  t: PropTypes.func.isRequired,
  page: PropTypes.string.isRequired,
  siteId: PropTypes.number,
};
CentreSummary.defaultProps = {
  siteId: null,
};
export default withTranslation()(CentreSummary);
